import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect, Suspense, lazy } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AdvertiserProvider, useAdvertiser } from "@/contexts/AdvertiserContext";
import { Layout } from "@/components/layout/Layout";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import ErrorBoundary from "@/components/ErrorBoundary";
import { captureReferral } from "@/lib/referral";

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="w-8 h-8 animate-spin text-gathrr-600 dark:text-gathrr-400" />
      <p className="text-sm text-stone-600 dark:text-zinc-400">Loading...</p>
    </div>
  </div>
);

// Pages - lazy loaded
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const GatePage = lazy(() => import("@/pages/GatePage"));
const HomePage = lazy(() => import("@/pages/HomePage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const PostDetailPage = lazy(() => import("@/pages/PostDetailPage"));
const MessagesPage = lazy(() => import("@/pages/MessagesPage"));
const DiscoverPage = lazy(() => import("@/pages/DiscoverPage"));
const CreatePage = lazy(() => import("@/pages/CreatePage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const ImpactPage = lazy(() => import("@/pages/ImpactPage"));
const WalletPage = lazy(() => import("@/pages/WalletPage"));
const OnboardingFlow = lazy(() => import("@/pages/onboarding/OnboardingFlow"));
const EmailPage = lazy(() => import("@/pages/EmailPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AdminLoginPage = lazy(() => import("@/pages/AdminLoginPage"));
const SpacesPage = lazy(() => import("@/pages/SpacesPage"));
const BusinessesPage = lazy(() => import("@/pages/BusinessesPage"));
const BusinessProfilePage = lazy(() => import("@/pages/BusinessProfilePage"));
const BusinessSetupPage = lazy(() => import("@/pages/BusinessSetupPage"));
const BusinessAppointmentsPage = lazy(() => import("@/pages/BusinessAppointmentsPage"));
const BusinessQuotesPage = lazy(() => import("@/pages/BusinessQuotesPage"));
const BusinessCustomersPage = lazy(() => import("@/pages/BusinessCustomersPage"));
const MyActivityPage = lazy(() => import("@/pages/MyActivityPage"));
const EventsPage = lazy(() => import("@/pages/EventsPage"));
const ModerationQueuePage = lazy(() => import("@/pages/ModerationQueuePage"));
const InvitesPage = lazy(() => import("@/pages/InvitesPage"));
const DonatePage = lazy(() => import("@/pages/donate/DonatePage"));
const CampaignPage = lazy(() => import("@/pages/donate/CampaignPage"));
const CreateCampaignPage = lazy(() => import("@/pages/donate/CreateCampaignPage"));
const VideoFeedPage = lazy(() => import("@/pages/VideoFeedPage"));
const VideoUploadPage = lazy(() => import("@/pages/VideoUploadPage"));
const VideoDetailPage = lazy(() => import("@/pages/VideoDetailPage"));
const BriefsPage = lazy(() => import("@/pages/BriefsPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));

// Advertiser Portal Pages - lazy loaded
const AdvertiserAuthPage = lazy(() =>
  import("@/pages/advertiser").then(m => ({ default: m.AdvertiserAuthPage }))
);
const AdvertiserDashboardPage = lazy(() =>
  import("@/pages/advertiser").then(m => ({ default: m.AdvertiserDashboardPage }))
);
const AdvertiserAnalyticsPage = lazy(() =>
  import("@/pages/advertiser").then(m => ({ default: m.AdvertiserAnalyticsPage }))
);
const AdvertiserLeadsPage = lazy(() =>
  import("@/pages/advertiser").then(m => ({ default: m.AdvertiserLeadsPage }))
);
const AdvertiserHelpPage = lazy(() =>
  import("@/pages/advertiser").then(m => ({ default: m.AdvertiserHelpPage }))
);
const AdvertiserTeamPage = lazy(() =>
  import("@/pages/advertiser").then(m => ({ default: m.AdvertiserTeamPage }))
);
const AdvertiserBillingPage = lazy(() =>
  import("@/pages/advertiser").then(m => ({ default: m.AdvertiserBillingPage }))
);
const AdvertiserCampaignsPage = lazy(() =>
  import("@/pages/advertiser").then(m => ({ default: m.AdvertiserCampaignsPage }))
);
const CampaignEditorPage = lazy(() =>
  import("@/pages/advertiser").then(m => ({ default: m.CampaignEditorPage }))
);
const FormLibrary = lazy(() =>
  import("@/pages/advertiser").then(m => ({ default: m.FormLibrary }))
);
const FormBuilder = lazy(() =>
  import("@/pages/advertiser").then(m => ({ default: m.FormBuilder }))
);
const FormResponses = lazy(() =>
  import("@/pages/advertiser").then(m => ({ default: m.FormResponses }))
);
const ProductManager = lazy(() =>
  import("@/pages/advertiser").then(m => ({ default: m.ProductManager }))
);
const OrdersPage = lazy(() =>
  import("@/pages/advertiser").then(m => ({ default: m.OrdersPage }))
);
const AdvertiserSettingsPage = lazy(() =>
  import("@/pages/advertiser").then(m => ({ default: m.AdvertiserSettingsPage }))
);

// Suspense wrapper component for routes
const SuspensePage = ({ children }) => (
  <Suspense fallback={<LoadingFallback />}>
    {children}
  </Suspense>
);

// Gate check wrapper - must pass gate before accessing any part of app
// Advertiser routes bypass the gate since they have their own auth system
const GateCheck = ({ children }) => {
  // Gate removed - direct access enabled
  return children;
};

const ProtectedRoute = ({ children, skipLayout = false, requireAdmin = false, allowOnboarding = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-rose-600" />
      </div>
    );
  }

  if (!user) {
    // If admin required, redirect to admin login
    if (requireAdmin) {
      return <Navigate to="/admin/login" replace />;
    }
    return <Navigate to="/auth" replace />;
  }

  // Check if onboarding is needed (new users must complete onboarding first)
  // Skip this check if we're on the onboarding page itself or allowOnboarding is true
  const isOnboardingPage = location.pathname === '/onboarding';
  if (!user.onboarding?.completed && !requireAdmin && !isOnboardingPage && !allowOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // Check admin access
  if (requireAdmin && !user.is_admin) {
    return <Navigate to="/admin/login" replace />;
  }

  if (skipLayout) {
    return children;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-rose-600" />
      </div>
    );
  }

  if (user) {
    // Check if onboarding is needed
    const onboardingCompleted = user.onboarding?.completed;
    if (!onboardingCompleted) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};

// Advertiser protected route - separate from main app auth
const AdvertiserProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAdvertiser();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100 dark:bg-zinc-950">
        <Loader2 className="w-8 h-8 animate-spin text-sprout-700" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/advertiser/login" replace />;
  }

  return children;
};

// Advertiser public route - redirect if already logged in
const AdvertiserPublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAdvertiser();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100 dark:bg-zinc-950">
        <Loader2 className="w-8 h-8 animate-spin text-sprout-700" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/advertiser/dashboard" replace />;
  }

  return children;
};

const AppRoutes = () => {
  // Pick up ?ref= wherever somebody lands, not just on the pages that use it.
  // A shared link can point at a cause, a campaign or the front door, and the
  // act it eventually leads to might be several clicks later.
  const location = useLocation();
  useEffect(() => {
    captureReferral(location.search);
  }, [location.search]);

  return (
    <Routes>
      <Route
        path="/auth"
        element={
          <PublicRoute>
            <SuspensePage>
              <AuthPage />
            </SuspensePage>
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <SuspensePage>
              <ForgotPasswordPage />
            </SuspensePage>
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <SuspensePage>
              <ResetPasswordPage />
            </SuspensePage>
          </PublicRoute>
        }
      />
      <Route
        path="/terms"
        element={<SuspensePage><LegalPage slug="terms" /></SuspensePage>}
      />
      <Route
        path="/privacy"
        element={<SuspensePage><LegalPage slug="privacy" /></SuspensePage>}
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute skipLayout>
            <SuspensePage>
              <OnboardingFlow />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      {/* Admin Portal Routes */}
      <Route
        path="/admin/login"
        element={
          <SuspensePage>
            <AdminLoginPage />
          </SuspensePage>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute skipLayout requireAdmin>
            <SuspensePage>
              <AdminPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <HomePage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <DashboardPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/discover"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <DiscoverPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/create"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <CreatePage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <MessagesPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/:username"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <ProfilePage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/post/:postId"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <PostDetailPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <SettingsPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/donate"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <DonatePage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/donate/create"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <CreateCampaignPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/donate/c/:slug"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <CampaignPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invites"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <InvitesPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/impact"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <ImpactPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      {/* /rewards was the old placeholder, and it taught the opposite of what
          FamCentral measures - points for logins, likes and streaks. Kept as a
          redirect rather than removed outright so any link already out there
          lands somewhere sensible. */}
      <Route path="/rewards" element={<Navigate to="/impact" replace />} />
      <Route
        path="/wallet"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <WalletPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/email"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <EmailPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/spaces"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <SpacesPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/businesses"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <BusinessesPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      {/* Creating one, and coming back to it. Same page: it reads the id, or
          the absence of one, from the route. */}
      <Route
        path="/businesses/new"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <BusinessSetupPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/businesses/:businessId/manage"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <BusinessSetupPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/businesses/:businessId/appointments"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <BusinessAppointmentsPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/businesses/:businessId/quotes"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <BusinessQuotesPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/businesses/:businessId/customers"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <BusinessCustomersPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my/activity"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <MyActivityPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      {/* What is on near you, and what you said you were coming to. */}
      <Route
        path="/events"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <EventsPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      {/* The review queue. The server refuses non-admins; this route only
          decides which page tries. */}
      <Route
        path="/admin/reports"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <ModerationQueuePage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      {/* A business has its own URL so it can be shared and linked to from a
          post, a deal or a message. Same page - it reads the slug. */}
      <Route
        path="/business/:slug"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <BusinessProfilePage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      {/* A cause has its own URL so it can be shared, bookmarked and survive
          a refresh. Same page - it reads the id from the route. */}
      <Route
        path="/spaces/:spaceId"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <SpacesPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/briefs"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <BriefsPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />

      {/* Video Feed Routes */}
      <Route
        path="/videos"
        element={
          <ProtectedRoute>
            <SuspensePage>
              <VideoFeedPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/videos/upload"
        element={
          <ProtectedRoute skipLayout>
            <SuspensePage>
              <VideoUploadPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/videos/:id"
        element={
          <ProtectedRoute skipLayout>
            <SuspensePage>
              <VideoDetailPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/videos/hashtag/:tag"
        element={
          <ProtectedRoute skipLayout>
            <SuspensePage>
              <VideoFeedPage />
            </SuspensePage>
          </ProtectedRoute>
        }
      />

      {/* Advertiser Portal Routes */}
      <Route
        path="/advertiser/login"
        element={
          <AdvertiserPublicRoute>
            <SuspensePage>
              <AdvertiserAuthPage />
            </SuspensePage>
          </AdvertiserPublicRoute>
        }
      />
      <Route
        path="/advertiser/register"
        element={
          <AdvertiserPublicRoute>
            <SuspensePage>
              <AdvertiserAuthPage />
            </SuspensePage>
          </AdvertiserPublicRoute>
        }
      />
      <Route
        path="/advertiser/dashboard"
        element={
          <AdvertiserProtectedRoute>
            <SuspensePage>
              <AdvertiserDashboardPage />
            </SuspensePage>
          </AdvertiserProtectedRoute>
        }
      />
      <Route
        path="/advertiser/team"
        element={
          <AdvertiserProtectedRoute>
            <SuspensePage>
              <AdvertiserTeamPage />
            </SuspensePage>
          </AdvertiserProtectedRoute>
        }
      />
      <Route
        path="/advertiser/billing"
        element={
          <AdvertiserProtectedRoute>
            <SuspensePage>
              <AdvertiserBillingPage />
            </SuspensePage>
          </AdvertiserProtectedRoute>
        }
      />
      <Route
        path="/advertiser/campaigns"
        element={
          <AdvertiserProtectedRoute>
            <SuspensePage>
              <AdvertiserCampaignsPage />
            </SuspensePage>
          </AdvertiserProtectedRoute>
        }
      />
      <Route
        path="/advertiser/campaigns/:campaignId/edit"
        element={
          <AdvertiserProtectedRoute>
            <SuspensePage>
              <CampaignEditorPage />
            </SuspensePage>
          </AdvertiserProtectedRoute>
        }
      />
      <Route
        path="/advertiser/campaigns/:campaignId"
        element={
          <AdvertiserProtectedRoute>
            <SuspensePage>
              <CampaignEditorPage />
            </SuspensePage>
          </AdvertiserProtectedRoute>
        }
      />
      <Route
        path="/advertiser/analytics"
        element={
          <AdvertiserProtectedRoute>
            <SuspensePage>
              <AdvertiserAnalyticsPage />
            </SuspensePage>
          </AdvertiserProtectedRoute>
        }
      />
      <Route
        path="/advertiser/leads"
        element={
          <AdvertiserProtectedRoute>
            <SuspensePage>
              <AdvertiserLeadsPage />
            </SuspensePage>
          </AdvertiserProtectedRoute>
        }
      />
      <Route
        path="/advertiser/forms"
        element={
          <AdvertiserProtectedRoute>
            <SuspensePage>
              <FormLibrary />
            </SuspensePage>
          </AdvertiserProtectedRoute>
        }
      />
      <Route
        path="/advertiser/forms/new"
        element={
          <AdvertiserProtectedRoute>
            <SuspensePage>
              <FormBuilder />
            </SuspensePage>
          </AdvertiserProtectedRoute>
        }
      />
      <Route
        path="/advertiser/forms/:formId/edit"
        element={
          <AdvertiserProtectedRoute>
            <SuspensePage>
              <FormBuilder />
            </SuspensePage>
          </AdvertiserProtectedRoute>
        }
      />
      <Route
        path="/advertiser/forms/:formId/responses"
        element={
          <AdvertiserProtectedRoute>
            <SuspensePage>
              <FormResponses />
            </SuspensePage>
          </AdvertiserProtectedRoute>
        }
      />
      <Route
        path="/advertiser/products"
        element={
          <AdvertiserProtectedRoute>
            <SuspensePage>
              <ProductManager />
            </SuspensePage>
          </AdvertiserProtectedRoute>
        }
      />
      <Route
        path="/advertiser/orders"
        element={
          <AdvertiserProtectedRoute>
            <SuspensePage>
              <OrdersPage />
            </SuspensePage>
          </AdvertiserProtectedRoute>
        }
      />
      <Route
        path="/advertiser/help"
        element={
          <AdvertiserProtectedRoute>
            <SuspensePage>
              <AdvertiserHelpPage />
            </SuspensePage>
          </AdvertiserProtectedRoute>
        }
      />
      <Route
        path="/advertiser/settings"
        element={
          <AdvertiserProtectedRoute>
            <SuspensePage>
              <AdvertiserSettingsPage />
            </SuspensePage>
          </AdvertiserProtectedRoute>
        }
      />
      <Route
        path="/advertiser"
        element={<Navigate to="/advertiser/login" replace />}
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <GateCheck>
          <AuthProvider>
            <AdvertiserProvider>
              <BrowserRouter>
                <AppRoutes />
                <Toaster position="top-center" />
              </BrowserRouter>
            </AdvertiserProvider>
          </AuthProvider>
        </GateCheck>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
